import { post, get } from "services";

// ── AI agent invocation ──────────────────────────────────────────────────────
// AWS Lambda is no longer invoked directly from the browser. Doing so required
// shipping AWS access keys in the JS bundle (readable by anyone who opens the
// site) — a full credential leak. Instead we call our own backend, which invokes
// the Lambda using its IAM task role (no static keys).
//
// These agents can run up to 15 min — longer than any HTTP/proxy timeout — so
// the backend returns a `jobId` immediately and we poll for the result. Every
// exported function below keeps its original signature and resolves with the
// SAME raw Lambda payload the direct invoke used to return, so callers need no
// changes.

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 16 * 60 * 1000;

// Backend replies are flat JSON; tolerate an axios interceptor that nests under
// `data` just in case.
const field = (res, name) =>
  res && res[name] !== undefined ? res[name] : res?.data?.[name];

async function pollJob(jobId) {
  const startedAt = Date.now();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await get(`system/agents/jobs/${jobId}`);
    const jobStatus = field(res, "jobStatus");

    if (jobStatus === "done") return field(res, "result") ?? null;
    if (jobStatus === "failed") {
      throw new Error(field(res, "error") || "Agent job failed");
    }
    if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
      throw new Error("Agent job timed out");
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
}

// Start an agent Lambda via the backend and resolve with its final result.
async function invokeAgent(key, payload) {
  const startRes = await post(`system/agents/${key}/start`, payload || {});
  const jobId = field(startRes, "jobId");
  if (!jobId) throw new Error("Failed to start agent job");
  return pollJob(jobId);
}

const mapFiles = (payload) =>
  (payload?.files || []).map((f) => ({
    name: f.name,
    mimetype: f.mimetype,
    base64: f.base64,
  }));

// Start pipeline (only function you need now)
export async function startBotAgent(payload) {
  const body = { tableName: payload?.table || "clearance" };
  return invokeAgent("bot", body);
}

export async function startVccBotAgent(payload) {
  return invokeAgent("vcc-upload", {
    tableName: payload?.table || "vcc-upload",
    files: mapFiles(payload),
  });
}

export async function startDeclarationBotAgent(payload) {
  return invokeAgent("vcc-upload", {
    tableName: payload?.table || "declration-upload",
    files: mapFiles(payload),
  });
}

export async function runDeclarationBotAgent(payload) {
  return invokeAgent("vcc-execution", {
    tableName: payload?.table || "both",
  });
}

export async function runVccBotAgent(payload) {
  return invokeAgent("vcc-execution", {
    tableName: payload?.table || "vcc-execution",
  });
}

export async function startReconcilationBotAgent(payload) {
  return invokeAgent("reconcile", {
    tableName: payload?.table || "bank_reconciles",
    payload,
  });
}

export async function startTTAgent() {
  return invokeAgent("tt", {});
}

export async function startUnAppliedFund(payload) {
  return invokeAgent("unapplied", { ...payload });
}

export async function startPrepaidAgent(payload) {
  return invokeAgent("prepaid", { ...payload });
}
